import React, {useEffect, useState} from "react";
import {changeStatus, create, deleteById, getById, update} from "../../services/liquidation.service";
import {getById as getUserById} from "../../services/user.service";
import {deleteById as deletePurchaseId} from "../../services/purchase.service";
import {NavigateFunction, NavLink, useNavigate, useParams} from "react-router-dom";
import {useFormik} from 'formik';
import {Button as MaterialButton, Chip, Chip as ChipMaterial, Link, TextField} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridSaveAltIcon,
  GridToolbarContainer
} from "@mui/x-data-grid";
import {ArrowBackIosNew} from "@mui/icons-material";
import showNotification from "../utils/Notify";
import {Box, Breadcrumbs, Button, Grid, Stack, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import moment, {Moment} from "moment/moment";
import ConfirmationModal from "../utils/ConfirmationModal";
import AddIcon from "@mui/icons-material/Add";
import IPurchase from "../../types/purchase.type";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import {PurchaseApprovalStatus} from "../../types/enumeration/PurchaseApprovalStatus";
import {PurchaseApprovalStatusNames} from "../../types/enumeration/PurchaseApprovalStatusNames";
import {PurchaseApprovalStatusColors} from "../../types/enumeration/PurchaseApprovalStatusColors";
import StatusHistoryModal from "../utils/StatusHistoryModal";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import {LiquidationStatus} from "../../types/enumeration/LiquidationStatus";
import ILiquidation from "../../types/liquidation.type";
import ILiquidationChangeStatus from "../../types/liquidation-change-status.type";
import {LiquidationStatusColors} from "../../types/enumeration/LiquidationStatusColors";
import {LiquidationStatusNames} from "../../types/enumeration/LiquidationStatusNames";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import * as Yup from "yup";
import {getCurrentUser} from "../../services/auth.service";

export const MyLiquidationForm: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const currentUser = getCurrentUser();
  const { liquidationId } = useParams();
  const [selectedNewStatus, setSelectedNewStatus] = React.useState<LiquidationStatus | null>(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [modalMessage, setModalMessage] = React.useState("");
  const [confirmationDeleteModalOpen, setConfirmationDeleteModalOpen] = React.useState(false);
  const [confirmationPurchaseDeleteModalOpen, setConfirmationPurchaseDeleteModalOpen] = React.useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = React.useState<any>();

  const initialValues: ILiquidation = {
    id: null,
    createdBy: null,
    reportedBy: null,
    comments: "",
    created: null,
    imputationPeriod: null,
    status: LiquidationStatus.IN_PROGRESS,
    purchases: [],
    statusHistory: []
  };

  const [liquidation, setLiquidation] = useState<ILiquidation>(initialValues);

  const onSubmit = (liquidation: ILiquidation) => {
    if (liquidationId) {
      update(liquidation.id, liquidation)
        .then(() => {
          showNotification({type: 'success', message: 'Liquidacion acutalidaza correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(liquidation)
        .then((response) => {
          showNotification({type: 'success', message: 'Liquidacion creada correctamente.'});
          if (liquidation.status === LiquidationStatus.IN_PROGRESS) {
            navigate('/my-liquidations/edit/' + response.data.id);
          } else {
            goBack();
          }
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const validationSchema = Yup.object({
    imputationPeriod: Yup.string().required('El Periodo de Imputacion es requerido'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const goBack = () => {
    navigate("/my-liquidations");
  };

  const columns: GridColDef<IPurchase>[] = [
    {
      field: 'invoiceNumber',
      headerName: 'Factura Numero',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'created',
      headerName: 'Fecha',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${moment(params.row.created).format('DD/MM/YYYY')}`}</span>;
      }
    },
    {
      field: 'invoiceType',
      headerName: 'Tipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{params.row.invoiceType?.name}</span>;
      }
    },
    {
      field: 'approvalStatus',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={PurchaseApprovalStatusColors[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     label={PurchaseApprovalStatusNames[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     sx={{width: 160, fontWeight: 'bold', height: 20, marginBottom: 0.5}}/>;
      }
    },
    {
      field: 'totalAmount',
      headerName: 'Monto Total',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >${`${params.row.totalAmount.toFixed(2)}`}</span>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.row)}
            disabled={formik.values.status === LiquidationStatus.SUBMITTED || formik.values.status === LiquidationStatus.CLOSE}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteForeverIcon />}
            label="Delete"
            className="textPrimary"
            onClick={handlePurchaseDeleteClick(params.row)}
            disabled={formik.values.status === LiquidationStatus.SUBMITTED || formik.values.status === LiquidationStatus.CLOSE}
            color="inherit"
          />
        ];
      },
    },
  ];

  const handleChangeImputationPeriod = (value: Moment | null) => {
    formik.setFieldValue('imputationPeriod', moment(value).format('YYYY-MM'));
  };

  const handleEditClick = (purchase: IPurchase) => () => {
    navigate('purchase/edit/' + purchase.id);
  };

  const handleAddClick = () => {
    if (liquidationId) {
      navigate('purchase/create');
    } else {
      showNotification({type: 'warning', message: 'Antes de agregar una compra, guarde primero la Liquidacion.'});
    }
  };

  const handlePurchaseDeleteClick = (purchase: IPurchase) => () => {
    setSelectedPurchaseId(purchase.id);
    setConfirmationPurchaseDeleteModalOpen(true);
  };

  const handleStatusHistoryModalOpen = () => {
    setStatusHistoryModalOpen(true);
  }

  const handleStatusHistoryModalClose = () => {
    setStatusHistoryModalOpen(false);
  }

  const handleConfirmationPurchaseDeleteModalClose = () => {
    setSelectedPurchaseId(null);
    setConfirmationPurchaseDeleteModalOpen(false);
  }

  const onConfirmPurchaseDelete = () => {
    deletePurchaseId(selectedPurchaseId)
      .then(() => {
        showNotification({type: 'success', message: 'Compra eliminada correctamente.'});
        handleConfirmationPurchaseDeleteModalClose();
        fetchLiquidation();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  const handleDeleteClick = () => () => {
    setConfirmationDeleteModalOpen(true);
  };

  const handleConfirmationDeleteModalClose = () => {
    setConfirmationDeleteModalOpen(false);
  }

  const onConfirmDelete = () => {
    deleteById(liquidationId)
      .then(() => {
        showNotification({type: 'success', message: 'Liquidacion eliminada correctamente.'});
        handleConfirmationDeleteModalClose();
        goBack();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <MaterialButton
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={formik.values.status === LiquidationStatus.SUBMITTED || formik.values.status === LiquidationStatus.CLOSE}
        >
          Agregar Compra
        </MaterialButton>
      </GridToolbarContainer>
    );
  }

  const onConfirm = (comments?: string) => {
    if (selectedNewStatus) {
      let newStatus: ILiquidationChangeStatus = {
        status: selectedNewStatus,
        comments: comments ? comments : '',
      };
      changeStatus(liquidation.id, newStatus)
        .then((response) => {
          if (selectedNewStatus === LiquidationStatus.SUBMITTED) {
            showNotification({type: 'success', message: "Caja Confirmada correctamente."});
            fetchLiquidation();
          } else {
            goBack();
            showNotification({type: 'success', message: "Rendicion de Caja enviada correctamente."});
          }
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }

    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  };

  const handleConfirmationModalClose = () => {
    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  }

  const handleOpenChangeStatusModal = (status: LiquidationStatus) => {
    setModalTitle("Enviar Liquidacion");
    setModalMessage("Al confirmar esta accion se enviara la Liquidacion. ")
    setSelectedNewStatus(status);
    setConfirmationModalOpen(true);
  };

  const fetchLiquidation = async () => {
    if (liquidationId) {
      getById(liquidationId).then( (response) => {
        let liquidationData = response.data;
        setLiquidation(liquidationData)

        const [year, month] = liquidationData.imputationPeriod ?? [];
        const imputationPeriod =
          year && month
            ? moment({ year, month: month - 1, day: 1 }).format('YYYY-MM')
            : null;

        formik.setValues({
          ...liquidationData,
          created: moment(liquidationData.created),
          imputationPeriod,
        });
      })
    } else {
      if (currentUser.id) {
        const response = await getUserById(currentUser.id);
        formik.setFieldValue('reportedBy', response.data)
      }
    }
  };

  useEffect(() => {
    fetchLiquidation();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 1 },
              width: '100%',
            }}
          >
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <CurrencyExchangeIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                <Link component={NavLink} to='/my-liquidations' underline={"none"}>Mis Liquidaciones</Link>
              </Typography>
              {liquidationId ?
                <Typography level="h4" component="h1">
                    Carga de Liquidacion
                </Typography>
                :
                <Typography level="h4" component="h1">
                  Nueva
                </Typography>
              }
            </Breadcrumbs>
            <Box>
              <CssVarsProvider>
                <ChipMaterial
                  color={LiquidationStatusColors[LiquidationStatus[formik.values.status as keyof typeof LiquidationStatus]]}
                  label={LiquidationStatusNames[LiquidationStatus[formik.values.status as keyof typeof LiquidationStatus]]}
                  sx={{width: 200, fontWeight: 'bold', fontSize: 15}}
                  icon={<ManageSearchIcon fontSize="medium" sx={{marginTop: -0.5}} />}
                  clickable={true}
                  onClick={() => handleStatusHistoryModalOpen()}
                />
              </CssVarsProvider>
            </Box>
          </Box>

          <ConfirmationModal
            open={confirmationModalOpen}
            onConfirm={(comments?: string) => { onConfirm(comments)}}
            onCancel={handleConfirmationModalClose}
            title={modalTitle}
            message={modalMessage}
            isDeleteAction={false}
            enableComments={true}
          />

          <ConfirmationModal
            open={confirmationDeleteModalOpen}
            onConfirm={onConfirmDelete}
            onCancel={handleConfirmationDeleteModalClose}
            title="Confirmacion"
            message="Esta seguro de eliminar esta Liquidacion?"
            isDeleteAction={true}
          />

          <ConfirmationModal
            open={confirmationPurchaseDeleteModalOpen}
            onConfirm={onConfirmPurchaseDelete}
            onCancel={handleConfirmationPurchaseDeleteModalClose}
            title="Confirmacion"
            message="Esta seguro de eliminar esta compra?"
            isDeleteAction={true}
          />

          <StatusHistoryModal
            open={statusHistoryModalOpen}
            title={"Historial de Estados de Caja"}
            history={liquidation.statusHistory}
            onCancel={handleStatusHistoryModalClose}
            historyType={"liquidation"}
          />

          <Box sx={{ width: '100%', typography: 'body1' }}>
            <form className="form" onSubmit={formik.handleSubmit}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                marginBlock={1}
                marginInline={0}
                sx={{ width: '100%' }}
              >
                <Grid xl={12} md={12} sm={12} xs={12} >
                  <CssVarsProvider>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        views={["year", "month"]}
                        name="start"
                        label="Periodo"
                        format="MM-YYYY"
                        value={formik.values.imputationPeriod ? moment(formik.values.imputationPeriod) : null }
                        onChange={handleChangeImputationPeriod}
                        readOnly={!!liquidationId}
                        slotProps={{
                          textField: {
                            variant: 'filled',
                            fullWidth: true,
                            onChange: formik.handleChange,
                            onBlur: formik.handleBlur,
                            error: formik.touched.imputationPeriod && Boolean(formik.errors.imputationPeriod),
                            InputLabelProps: {
                              shrink:  true
                            }
                          }
                        }} />
                    </LocalizationProvider>
                    {formik.touched.imputationPeriod && formik.errors.imputationPeriod ? (
                      <span className="Error-alert">{formik.errors.imputationPeriod}</span>
                    ) : null}
                  </CssVarsProvider>
                </Grid>

                <Grid xl={12} md={12} sm={12} xs={12} >
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="comments"
                      label="Comentarios"
                      name="comments"
                      variant="filled"
                      type={"text"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.comments}
                      error={!!(formik.touched.comments && formik.errors.comments)}
                      slotProps={{ inputLabel: {shrink: true}, input: {readOnly: !!liquidationId}}}
                      multiline
                      rows={6}
                      sx={{mb: '3px'}}
                    />
                  </CssVarsProvider>
                </Grid>
              </Grid>

              <Box sx={{ width: '100%', height: 384, typography: 'body1' }} ml={1} mb={2} pr={2}>
                <CssVarsProvider>
                  <DataGrid
                    slots={{toolbar: EditToolbar}}
                    rows={liquidation.purchases}
                    columns={columns}
                    rowHeight={25}
                    columnHeaderHeight={30}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    pageSizeOptions={[10]}
                    localeText={{ noRowsLabel: "No Hay Compras Registradas" }}
                    disableRowSelectionOnClick
                  />
                </CssVarsProvider>
              </Box>

              <Stack direction="row" justifyContent="space-between" spacing={2} pr={1} pl={1}>
                <Stack direction="row" justifyContent="flex-start" spacing={2}>
                  {liquidationId && formik.values.status === LiquidationStatus.IN_PROGRESS &&
                      <Button
                          onClick={handleDeleteClick()}
                          variant="solid"
                          color="danger"
                          endDecorator={<DeleteForeverIcon />}
                      >
                          Eliminar Liquidacion
                      </Button>
                  }
                </Stack>
                <Stack direction="row" justifyContent="flex-end" spacing={2}  pr={1} pl={1}>
                  <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
                  {liquidationId && (formik.values.status === LiquidationStatus.IN_PROGRESS || formik.values.status === LiquidationStatus.TO_FIX) &&
                      <Button
                          onClick={() => handleOpenChangeStatusModal(LiquidationStatus.SUBMITTED)}
                          variant="solid"
                          color="primary"
                          endDecorator={<CheckCircleIcon />}
                      >
                          Enviar Liqiudacion
                      </Button>
                  }
                  {!liquidationId &&
                      <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
                  }
                </Stack>
              </Stack>
            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};


